import { useState } from 'react'
import type { StepTask } from '../../types'
import { Checkbox, Field, ReadonlyValue, SectionHeader, getParams } from './fields'
import { KYC_PROVIDER_LABELS, KycProposedConfig, connectorFor } from './KycProposedConfig'

export interface KycDrawerProps {
  task: StepTask
  onChange: (updater: (t: StepTask) => StepTask) => void
}

export function KycDrawer({ task }: KycDrawerProps) {
  const params = getParams(task)
  // Real workflow JSON stores these as rulesetName / providerName. Fall back to
  // the legacy keys only if the real ones are absent.
  const ruleset = (params.rulesetName as string | undefined) ?? (params.ruleset as string | undefined) ?? ''
  const provider = (params.providerName as string | undefined) ?? (params.provider as string | undefined) ?? ''
  const profileName = (params.name as string | undefined) ?? ''

  // Pre-check whichever provider the real workflow JSON already names (if
  // recognised) — otherwise nothing's checked until the rep picks one. This
  // node's whole point is showing a cascade, so more than one can be selected.
  const derivedConnector = provider ? connectorFor(provider) : 'unknown'
  const derived = KYC_PROVIDER_LABELS.find((label) => connectorFor(label) === derivedConnector)
  const [selectedProviders, setSelectedProviders] = useState<string[]>(derived ? [derived] : [])

  function toggleProvider(label: string) {
    setSelectedProviders((prev) => (prev.includes(label) ? prev.filter((p) => p !== label) : [...prev, label]))
  }

  return (
    <div className="flex flex-col gap-4">
      <SectionHeader title="Existing ops center workflow config" />

      <Field label="Profile Name">
        <ReadonlyValue>{profileName || '—'}</ReadonlyValue>
      </Field>

      <Field label="Ruleset Name">
        <ReadonlyValue>{ruleset || '—'}</ReadonlyValue>
      </Field>

      <Field label="Provider Name">
        <ReadonlyValue>{provider || '—'}</ReadonlyValue>
      </Field>

      <SectionHeader title="Proposed additional config" hint="Mockup · not saved" />
      <p className="text-[10px] text-[#5b6b85] -mt-2">
        Grouped by the srv_kycutility config file each setting lives in.
      </p>

      <Field label="KYC Providers">
        <div className="grid grid-cols-2 gap-x-3 gap-y-1">
          {KYC_PROVIDER_LABELS.map((label) => (
            <Checkbox
              key={label}
              label={label}
              checked={selectedProviders.includes(label)}
              onChange={() => toggleProvider(label)}
            />
          ))}
        </div>
      </Field>

      <KycProposedConfig providers={selectedProviders} />
    </div>
  )
}
